import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { flatten, get, keys, merge, pick, values, zipObject } from 'lodash';
import { normalize, Schema } from 'normalizr';

import { AnyObject } from '../../../app-core';
import { NormalisedData } from '../../../redux-helpers/types';
import { Credentials } from '../../../signer/types';
import { createActionSet } from '../../helpers/actions';
import { ActionSet, FreeShapeAction, Meta } from '../../types';
import { API_REQUEST } from './constants';
import {
  ApiActionError,
  ApiActionResponse,
  ApiFlaggedAction,
  ApiFlaggedMeta,
  ApiFlaggedPayload,
  SignRequest
} from './types';

/**
 * Checks if the action is a api flagged action.
 * @param {FreeShapeAction} action accepted middleware action.
 * @returns {boolean}
 */
export const isApiRequest = (action: FreeShapeAction): action is ApiFlaggedAction => get(action, 'meta.flag') === API_REQUEST;

/**
 * Gets the schema from the action.
 * @param {ApiFlaggedAction} action
 * @returns {Schema}
 */
export const getSchema = (action: ApiFlaggedAction): Schema => action.meta.schema;

/**
 * Merges the request configuration from the action payload with the default axios client.
 * @param {AxiosRequestConfig} clientConfig
 * @param {ApiFlaggedAction} action
 * @returns {AxiosRequestConfig}
 */
export const mergeRequestConfig = (clientConfig: AxiosRequestConfig, action: ApiFlaggedAction): AxiosRequestConfig => {
  const defaultConfig = pick(clientConfig, ['baseURL']);

  return merge({}, defaultConfig, action.payload.request);
};

/**
 * Gets the client name from the action.
 * @param {ApiFlaggedAction} action
 * @returns {ApiFlaggedPayload.client}
 */
export const getClientName = (action: ApiFlaggedAction): ApiFlaggedPayload['client'] => action.payload.client;

/**
 * Removes the API metadata cleaning the action.
 * @param {ApiFlaggedMeta} meta
 * @returns {Meta}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const removeApiMetadata = ({ flag, ...filteredMeta }: ApiFlaggedMeta): Meta => filteredMeta;

/**
 * Gets a pending action to be delivered.
 * @param {ApiFlaggedAction} action
 * @returns {FreeShapeAction}
 */
export const getPendingAction = (action: ApiFlaggedAction): FreeShapeAction => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeApiMetadata(action.meta);

  return {
    meta,
    type: actionTypes.PENDING
  };
};

/**
 * Gets a fullifed action to be delivered.
 * @param {ApiFlaggedAction} action
 * @param {AxiosResponse} response
 * @returns {ApiActionResponse}
 */
export const getFulfilledAction = (action: ApiFlaggedAction, response: AxiosResponse): ApiActionResponse => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeApiMetadata(action.meta);

  return {
    meta,
    type: actionTypes.FULFILLED,
    payload: response
  };
};

/**
 * Gets a rejected action to be delivered.
 * @param {ApiFlaggedAction} action
 * @param {AxiosError} error
 * @returns {ApiActionError}
 */
export const getRejectedAction = (action: ApiFlaggedAction, error: AxiosError): ApiActionError => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeApiMetadata(action.meta);

  return {
    meta,
    type: actionTypes.REJECTED,
    payload: error,
    error: true
  };
};

/**
 * Normalise the data with the schema to save it into the Redux state.
 * Do nothing if no schema provided.
 *
 * @function normaliseData
 * @param {AnyObject | []} data
 * @param {Schema} schema
 * @return {NormalisedData<unknown>}
 */
export const normaliseData = (data: unknown[] | AnyObject, schema: Schema): NormalisedData<unknown> => {
  const getData = get(data, 'data') || data;
  return normalize(getData, schema);
};

/**
 * Preprocesses a request in order to make it stable for making a request to the API Gateway.
 * @param {AxiosRequestConfig} requestConfig
 * @param {Signer<SC, SR>} signer
 * @param {Authenticator<AC, AE>} authenticator
 * @returns signed request
 */
export const preprocessRequest: SignRequest = async (requestConfig, signer, authenticator) => {
  const creds = await authenticator.fetchCredentials();

  const signedRequestConfig = signer.sign(requestConfig, creds as Credentials);

  signedRequestConfig.cleanHeaders();

  return signedRequestConfig.getConfig();
};

export const getDataFromAPIMiddlewareAction = (action: unknown) => get(action, 'payload');

export const getDataFromAPIMiddlewareActionList = (actions: unknown[]) => flatten(
  actions.map((action: unknown) => get(action, 'payload'))
);

export const getDataFromAPIMiddlewareActionsMap = <T = unknown>(actionsMap: unknown): T => {
  const keyList = keys(actionsMap);
  const results = values(actionsMap).map((action: unknown) => get(action, 'payload'));

  return zipObject(keyList, results) as T;
};
